import React from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import { propsToDom } from 'core/helpers';
import { Image } from 'components/Image';
import { Title1 } from 'styles/typography';
import { ContainerGrid } from 'components/Container';
import { Button } from 'components/Button';
import { useGlobalContext } from 'store/GlobalProvider';
import media from 'styles/media';
import { Panel } from 'components/Panel';
import { useSplitting } from 'hooks/useSplitting';
import { splittedRevealCSS } from 'styles/others';
import useOffsetSize from 'hooks/useOffsetSize';
import { motion, useScroll, useTransform } from 'framer-motion';
import classNames from 'classnames';

const HeroComponent = ({ className, primary, slice_type, next_slice_type, ...others }) => {
  const { isMobile } = useGlobalContext();
  const [{ height }, $module] = useOffsetSize();
  const $title = useSplitting();
  const { background, main_title, button, button_text, button_color, image } = primary;
  const hasImage = !!image?.url;
  const hasText = !!main_title?.text;
  const hasButton = !!button?.url && !!button_text;
  const imageProps = isMobile ? image?.thumbnails?.mobile : image;

  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, height], [0, height]);
  const opacity = useTransform(scrollY, [0, height], [1, 0]);

  return (
    <Panel
      className={classNames(className, { 'has-image': hasImage })}
      {...propsToDom(others)}
      ref={$module}
      id={slice_type}
      nextId={next_slice_type}
      background={background}
      style={{ '--max-height': height > 0 ? `${height}px` : null }}
    >
      <Absolute style={hasImage ? {} : { y, opacity }}>
        <ContainerGrid>
          <Content>
            {hasText && <Title1 ref={$title}>{main_title.text}</Title1>}
            {hasButton && (
              <Button {...button} color={button_color} icon="arrow-right">
                {button_text}
              </Button>
            )}
          </Content>
        </ContainerGrid>
      </Absolute>
      {hasImage && <Image {...imageProps} sizes="100vw" animation="blur" />}
    </Panel>
  );
};

const Content = styled.div`
  grid-column-end: span 5;
  padding-bottom: var(--margin);

  ${Button} {
    margin-top: var(--margin);
  }

  ${Title1} {
    ${splittedRevealCSS};
  }

  ${media.mobile`
    grid-column-end: span 6;
  `}
`;

const Absolute = styled(motion.div)`
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 2;

  .has-image & {
    position: sticky;
    top: 0;
    bottom: auto;
    height: min(var(--max-height), var(--viewport-height, 100vh));
  }
`;

export const Hero = styled(HeroComponent)`
  position: relative;

  &:not(.has-image) {
    min-height: var(--viewport-height, 100vh);
    overflow: hidden;
  }

  ${Image} {
    margin-top: calc(min(var(--max-height), var(--viewport-height, 100vh)) * -1);
  }

  ${ContainerGrid} {
    position: absolute;
    left: 0;
    bottom: 0;
  }
`;

export const query = graphql`
  fragment SliceHeroHome on PrismicHomeDataBodyHero {
    id
    slice_type
    primary {
      background
      main_title {
        text
      }
      button {
        url
        target
      }
      button_text
      button_color
      image {
        url
        alt
        dimensions {
          width
          height
        }
        thumbnails {
          mobile {
            url
            alt
            dimensions {
              width
              height
            }
          }
        }
      }
    }
  }

  fragment SliceHeroPage on PrismicPageDataBodyHero {
    id
    slice_type
    primary {
      background
      main_title {
        text
      }
      button {
        url
        target
      }
      button_text
      button_color
      image {
        url
        alt
        dimensions {
          width
          height
        }
        thumbnails {
          mobile {
            url
            alt
            dimensions {
              width
              height
            }
          }
        }
      }
    }
  }
`;
